import dayjs from 'dayjs';
import {
  ClientResponse,
  EndClientAbsenceForAppointmentRequest,
  EventResponse,
  PlanningEntityType,
  RemoveAssignmentFromAppointmentsRequest
} from 'generated/index';
import { useNavParams, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { PlanningEntityMap, Schedule } from '../../types';

import { View } from '../../enums';

import { mutations } from 'RestAPIs/rapini';

import {
  assignEmploymentEditAppointmentTypeAtom,
  dialogActiveAtom,
  planResponsePlanningEntityIdentifiersAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from '../../atoms';

import {
  ConfirmationDialog,
  LoadingOverlay,
  showSnackbar
} from 'components/shared';
import { showSuccessSnackbar } from 'components/shared/Snackbar';

import useCopyMode from '../../hooks/useCopyMode';
import { useEndAppointments } from '../../mutations';
import AddAbsenceDialog from '../AddAbsenceDialog';
import AddEditAppointmentDrawer from '../AddEditAppointmentDrawer/AddEditAppointmentDrawer';
import AssignEmploymentDialog from '../AssignEmploymentDialog/AssignEmploymentDialog';
import ClientAbsencePeriodsDialog from '../ClientAbsencePeriodsDialog';
import {
  findContainer,
  getClientName,
  refetchPlanResponsePlanningEntityIdentifiers
} from '../../utils';
import { useEffect, useMemo, useState } from 'react';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { useSessionStorage } from 'usehooks-ts';
import { LISTVIEW_SESSION_STORAGE_KEYS } from '../../ListView/types';

enum Options {
  END_APPOINTMENT = 'endAppointment',
  MANAGE_CLIENT_ABSENCE_PERIODS = 'manageClientAbsencePeriods',
  SHOW_PLANNING_CLIENT = 'showPlanningClient',
  NAVIGATE_TO_ECD_CLIENT = 'navigateToECDClient',
  SET_APPOINTMENT_ABSENT = 'setAppointmentAbsent',
  SET_APPOINTMENT_PRESENT = 'setAppointmentPresent',
  EDIT_APPOINTMENT = 'editAppointment',
  COPY_APPOINTMENT = 'copyAppointment',
  DELETE_APPOINTMENT = 'deleteAppointment',
  CREDIT_APPOINTMENT = 'creditAppointment',
  ASSIGN_EMPLOYMENT = 'assignEmployment',
  UNASSIGN_EMPLOYMENT = 'unassignEmployment',
  UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING = 'unassignAppointment'
}

type Props = {
  planningEntities?: PlanningEntityMap;
  isEmployeePerspective?: boolean;
};

export default function AppointmentContextMenuManager({
  planningEntities,
  isEmployeePerspective
}: Props) {
  const [selectedContextMenuOption, setSelectedContextMenuOption] =
    useRecoilState(selectedContextMenuOptionAtom);

  const [hasAbsencesConflict, setHasAbsencesConflict] = useState(false);

  const selectedOption = selectedContextMenuOption?.option;

  const { event, events } = useMemo(() => {
    const initialState = {
      event: undefined,
      events: undefined
    };

    if (planningEntities && selectedContextMenuOption?.id) {
      const container = findContainer(
        planningEntities,
        selectedContextMenuOption.id
      );

      if (!container) return initialState;

      const appointmentGrouping = planningEntities[container];
      const agTypesAllowed: PlanningEntityType[] = [
        'ROUTE',
        'EMPLOYMENT',
        'EMPLOYEE'
      ];
      const events =
        agTypesAllowed.includes(appointmentGrouping.type) &&
        appointmentGrouping.id !== 'to-plan'
          ? appointmentGrouping?.schedules?.flatMap(
              (s: Schedule) => s.events
            ) ?? []
          : appointmentGrouping?.events ?? [];

      const event = events.find(
        ({ id, scheduleStart }) =>
          id === selectedContextMenuOption.id &&
          (scheduleStart && selectedContextMenuOption.scheduleStart
            ? dayjs(selectedContextMenuOption.scheduleStart).format(
                'YYYY-MM-DD'
              ) === dayjs(scheduleStart).format('YYYY-MM-DD')
            : true)
      );

      return {
        event,
        events
      };
    }

    return initialState;
  }, [selectedContextMenuOption]);

  const id = event?.id ?? '';
  const start = event?.start ?? '';
  const scheduleStart = event?.scheduleStart ?? '';
  const { t: common } = useTranslation('common');
  const t = (key: string, options?: { [key: string]: string }) =>
    common(`activityPlanning.${key}`, options);
  const backendErrorTranslation = (key: string) =>
    common(`backend_error.${key}`);

  const { setParams } = useNavParams();

  const [, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );
  const setDialogActive = useSetRecoilState(dialogActiveAtom);
  const { startCopyMode, endCopyMode } = useCopyMode();
  const { userDetails } = useSelector((state) => state);
  const [, setFilteredClientIds] = useSessionStorage<ClientResponse[]>(
    LISTVIEW_SESSION_STORAGE_KEYS.FILTERED_CLIENTS,
    []
  );
  const [
    assignEmploymentEditAppointmentType,
    setAssignEmploymentEditAppointmentType
  ] = useRecoilState<EditAppointmentType | null | undefined>(
    assignEmploymentEditAppointmentTypeAtom
  );

  const ECDUrl = userDetails?.sdbSsoApps?.['SDB ECD'];

  const client = event?.clients[0];
  const clientId = client?.id;
  const clientName = getClientName(client);

  const clientUrl =
    ECDUrl && clientId
      ? `${ECDUrl}/Clients/Client/Open?clientId=${clientId}`
      : '';

  const queryClient = useQueryClient();

  const { mutate: endAppointments, isLoading: isLoadingEndAppointments } =
    useEndAppointments();

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();
  const [
    isLoadingUnassignAppointment,
    setIsLoadingUnassignAppointmentFromEmployee
  ] = useState<boolean>(false);

  const { mutate: deleteAppointment, isLoading: isLoadingDeleteAppointment } =
    mutations.useDeleteAppointment();

  const { mutate: creditAppointment, isLoading: isLoadingCreditAppointment } =
    mutations.useCreditAppointments();

  const {
    mutateAsync: endClientAbsenceForAppointmentAsync,
    isLoading: isLoadingEndClientAbsenceForAppointment
  } = mutations.useEndClientAbsenceForAppointment();

  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const isLoading =
    isLoadingEndAppointments ||
    isLoadingDeleteAppointment ||
    isLoadingCreditAppointment ||
    isLoadingUnassignAppointment ||
    isLoadingEndClientAbsenceForAppointment;

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    const payload: RemoveAssignmentFromAppointmentsRequest = {
      appointmentOccurrences: [
        {
          entityId: id,
          originalOccurrenceStart: start,
          schedulingLabel: event!.schedulingLabel
        }
      ],

      editAppointmentType
    };

    setIsLoadingUnassignAppointmentFromEmployee(true);
    unassignAppointmentAsync(payload)
      .then(() => queryClient.invalidateQueries('planning'))
      .finally(() => setIsLoadingUnassignAppointmentFromEmployee(false));
  }

  useEffect(() => {
    if (selectedOption) {
      endCopyMode();

      if (
        selectedOption === t(Options.SET_APPOINTMENT_ABSENT) ||
        selectedOption === t(Options.MANAGE_CLIENT_ABSENCE_PERIODS) ||
        selectedOption === t(Options.EDIT_APPOINTMENT) ||
        selectedOption === t(Options.ASSIGN_EMPLOYMENT) ||
        selectedOption === t(Options.END_APPOINTMENT) ||
        selectedOption === t(Options.UNASSIGN_EMPLOYMENT)
      ) {
        setDialogActive(true);
        return;
      }

      if (selectedOption === t(Options.SHOW_PLANNING_CLIENT) && !!client) {
        setFilteredClientIds([client]);
        setParams({ view: View.List });
        setSelectedContextMenuOption(null);
      }

      if (selectedOption === t(Options.COPY_APPOINTMENT)) {
        startCopyMode(event as EventResponse);
        setSelectedContextMenuOption(null);
      }

      if (selectedOption === t(Options.NAVIGATE_TO_ECD_CLIENT)) {
        window.open(clientUrl, '_blank');
        setSelectedContextMenuOption(null);
      }

      if (
        selectedOption ===
          t(Options.UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING) &&
        event?.isRecurring
      ) {
        setShouldShowPopoverDialog({
          id,
          scheduleStart: scheduleStart
            ? dayjs(scheduleStart).format('YYYY-MM-DD')
            : '',
          isUnassignment: true
        });
        setSelectedContextMenuOption(null); // Set manually, as the popoverdialog can't call handleDialogClose()
      }

      if (
        selectedOption ===
          t(Options.UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING) &&
        !event?.isRecurring
      ) {
        unassignAppointment('ONLY_THIS');
        setSelectedContextMenuOption(null);
      }

      if (selectedOption === t(Options.SET_APPOINTMENT_PRESENT)) {
        const payload: EndClientAbsenceForAppointmentRequest = {
          appointmentOccurrence: {
            entityId: id,
            originalOccurrenceStart: start,
            schedulingLabel: event!.schedulingLabel
          }
        };
        endClientAbsenceForAppointmentAsync(payload, {
          onSuccess: () => {
            refetchPlanResponsePlanningEntityIdentifiers(
              setPlanResponsePlanningEntityIdentifiers
            );
            setSelectedContextMenuOption(null);
            showSuccessSnackbar;
          }
        }).catch((errorResponse) => {
          const errorMessage = errorResponse?.response?.data?.error?.message;
          if (errorResponse?.request?.status == 409) {
            setDialogActive(true);
            setHasAbsencesConflict(true);
            showSnackbar({
              severity: 'warning',
              message: errorMessage
                ? backendErrorTranslation(errorMessage)
                : t('somethingWentWrong')
            });
          } else {
            showSnackbar({
              severity: 'error',
              message: t('somethingWentWrong')
            });
          }
        });
      }
    }
  }, [selectedOption]);

  function handleDialogClose() {
    setSelectedContextMenuOption(null);
    setDialogActive(false);
    setHasAbsencesConflict(false);
  }

  useEffect(() => {
    if (selectedOption === t(Options.ASSIGN_EMPLOYMENT)) {
      if (event?.isRecurring) {
        setShouldShowPopoverDialog({
          id,
          isAssignEmployment: true,
          scheduleStart: scheduleStart
            ? dayjs(scheduleStart).format('YYYY-MM-DD')
            : ''
        });
      } else {
        setAssignEmploymentEditAppointmentType('ONLY_THIS');
      }
    }
  }, [selectedOption]);

  return (
    <>
      {selectedOption ===
        t(Options.UNASSIGN_APPOINTMENT_FROM_APPOINTMENT_GROUPING) &&
        event?.isRecurring}
      {selectedOption === t(Options.END_APPOINTMENT) && (
        <ConfirmationDialog
          cancelBtnText={common('no')}
          confirmBtnText={common('yes')}
          title={t('endAppointmentDialog.title')}
          type="warning"
          handleAction={(isConfirmed) => {
            if (isConfirmed) {
              if (selectedOption === t(Options.END_APPOINTMENT)) {
                endAppointments(
                  {
                    appointmentIds: [id],
                    date: start
                  },
                  {
                    onSuccess: () => {
                      showSuccessSnackbar();
                      refetchPlanResponsePlanningEntityIdentifiers(
                        setPlanResponsePlanningEntityIdentifiers
                      );
                    }
                  }
                );
              }
            }
            handleDialogClose();
          }}
        >
          <p>{t('endAppointmentDialog.body')}</p>
        </ConfirmationDialog>
      )}
      {selectedOption === t(Options.SET_APPOINTMENT_ABSENT) && ( // TODO: decouple form from dialog so data gets fetched on dialog open, e.g. AddEditAppointmentDrawer
        <AddAbsenceDialog
          open={selectedOption === t(Options.SET_APPOINTMENT_ABSENT)}
          title={t('absenceDialogTitle', { clientName })}
          appointmentId={id}
          appointmentStart={start}
          clientId={clientId}
          clientName={clientName as string}
          handleClose={handleDialogClose}
        />
      )}
      {(selectedOption === t(Options.MANAGE_CLIENT_ABSENCE_PERIODS) ||
        (hasAbsencesConflict && event !== undefined)) && (
        <ClientAbsencePeriodsDialog
          open={
            selectedOption === t(Options.MANAGE_CLIENT_ABSENCE_PERIODS) ||
            hasAbsencesConflict
          }
          appointmentStart={start}
          clientName={clientName as string}
          handleClose={handleDialogClose}
          clientId={clientId}
        />
      )}
      <AddEditAppointmentDrawer
        open={selectedOption === t(Options.EDIT_APPOINTMENT)}
        handleClose={handleDialogClose}
        appointment={event}
        events={events}
        schedulingLabel={
          event?.schedulingLabel === 'WORKPLACE' ? 'WORKPLACE' : 'VV'
        }
        isEmployeePerspective={isEmployeePerspective}
      />
      {!!assignEmploymentEditAppointmentType && (
        <AssignEmploymentDialog
          open={!!assignEmploymentEditAppointmentType}
          event={event}
          date={start}
          onClose={handleDialogClose}
          editAppointmentType={assignEmploymentEditAppointmentType}
        />
      )}
      {selectedOption === t(Options.UNASSIGN_EMPLOYMENT) && (
        <ConfirmationDialog
          cancelBtnText={common('no')}
          confirmBtnText={common('yes')}
          title={t('unassignAppointmentDialog.title')}
          type="warning"
          handleAction={(isConfirmed) => {
            if (
              isConfirmed &&
              selectedOption === t(Options.UNASSIGN_EMPLOYMENT)
            )
              unassignAppointment('ONLY_THIS');
            handleDialogClose();
          }}
        >
          <p>{t('unassignAppointmentDialog.body')}</p>
        </ConfirmationDialog>
      )}
      {selectedOption === t(Options.DELETE_APPOINTMENT) && (
        <ConfirmationDialog
          cancelBtnText={common('no')}
          confirmBtnText={common('yes')}
          title={t('deleteAppointmentDialog.title')}
          type="warning"
          handleAction={(isConfirmed) => {
            if (isConfirmed) {
              if (selectedOption === t(Options.DELETE_APPOINTMENT)) {
                deleteAppointment(
                  {
                    appointmentOccurrenceIdentifier: {
                      entityId: id,
                      originalOccurrenceStart: dayjs(start).format('YYYY-MM-DD')
                    },
                    editAppointmentType: 'ONLY_THIS'
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries('planning');
                      showSuccessSnackbar();
                    },
                    onError: () => {
                      const message = t('somethingWentWrong');
                      showSnackbar({
                        severity: 'error',
                        message: message
                      });
                    }
                  }
                );
              }
            }
            handleDialogClose();
          }}
        >
          <p>{t('deleteAppointmentDialog.body')}</p>
        </ConfirmationDialog>
      )}
      {selectedOption === t(Options.CREDIT_APPOINTMENT) && (
        <ConfirmationDialog
          cancelBtnText={common('no')}
          confirmBtnText={common('yes')}
          title={t('creditAppointmentDialog.title')}
          type="warning"
          handleAction={(isConfirmed) => {
            if (isConfirmed) {
              if (selectedOption === t(Options.CREDIT_APPOINTMENT)) {
                creditAppointment(
                  {
                    appointmentEntityIds: [id]
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries('planning');
                      showSuccessSnackbar();
                    },
                    onError: () => {
                      const message = t('somethingWentWrong');
                      showSnackbar({
                        severity: 'error',
                        message: message
                      });
                    }
                  }
                );
              }
            }
            handleDialogClose();
          }}
        >
          <p>{t('creditAppointmentDialog.body')}</p>
        </ConfirmationDialog>
      )}
      {isLoading && <LoadingOverlay loading progressSize={20} />}
    </>
  );
}
